import {
  ProfileFieldsFragment,
  useJoinOrganizationMutation,
  ViewerDocument,
} from '@/generated/graphql';
import { formatGraphQLErrors } from '@/utils/apollo-errors';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-material-ui';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import * as yup from 'yup';

const useStyles = makeStyles(() => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
  },
}));

const JoinSchema = yup.object().shape({
  code: yup
    .string()
    .required('Required')
    .min(4, 'Invalid code')
    .max(12, 'Invalid code'),
});

export interface JoinFields {
  code: string;
}

const initialValues: JoinFields = {
  code: '',
};

interface JoinOrganizationDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (profile: ProfileFieldsFragment) => void;
}

export default function JoinOrganizationDialog({
  open,
  onClose,
  onSuccess,
}: JoinOrganizationDialogProps): JSX.Element {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState<string[]>([]);

  const [joinOrganization, { loading }] = useJoinOrganizationMutation();

  function handleClose() {
    setError([]);
    onClose();
  }

  async function handleSubmit(values: JoinFields) {
    if (loading) {
      return;
    }

    const input = {
      ...values,
    };

    try {
      const { data, errors } = await joinOrganization({
        variables: { input },
        refetchQueries: [{ query: ViewerDocument }],
        awaitRefetchQueries: true,
      });

      const profile = data?.joinOrganization;

      if (errors?.length || !profile) {
        setError(
          formatGraphQLErrors(errors) ?? ['Error joining the organization'],
        );
      } else {
        enqueueSnackbar('Successfully joined the organization');
        onSuccess(profile);
        handleClose();
      }
    } catch (err) {
      setError(formatGraphQLErrors(err.graphQLErrors) ?? [err.message]);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="join-org-dialog-title"
      fullScreen={fullScreen}
      maxWidth="xs"
      fullWidth
    >
      <Formik
        initialValues={initialValues}
        validationSchema={JoinSchema}
        onSubmit={handleSubmit}
      >
        {({ dirty, isValid }) => (
          <Form id="join-org-form" className={classes.form}>
            <DialogTitle id="join-org-dialog-title">
              Join an organization
            </DialogTitle>
            <DialogContent dividers>
              {error.length > 0 &&
                error.map((err) => (
                  <Box key={err} mb={1}>
                    <Alert severity="error" variant="outlined">
                      {err}
                    </Alert>
                  </Box>
                ))}

              <Box my={2}>
                <Field
                  component={TextField}
                  name="code"
                  label="Invitation code"
                  variant="outlined"
                  fullWidth
                  autoFocus
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                disabled={loading}
                color="primary"
                size="large"
              >
                Cancel
              </Button>
              <Button
                variant="text"
                color="primary"
                size="large"
                disabled={!dirty || !isValid || loading}
                type="submit"
                form="join-org-form"
              >
                Submit
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
